<template>
    <MainComponent data-testid="profile-reputation">
        <template #title>
            <h2>{{ t('profile.section.reputation.title') }}</h2>
        </template>
        <template v-if="profile?.editMode && hasReputation" #title-action>
            <VJoyIconButton icon="pen" variant="ghost" size="small" :label="$t('general.edit')" @click="editReputationWidgets" />
        </template>
        <template #content>
            <div v-if="hasReputation" class="profile-reputation-widgets">
                <div v-for="reputation in reputations" :key="reputation.providerName" class="profile-reputation-widgets__widget">
                    <a :href="reputation.externalUserPageUrl" :target="getTarget(reputation)" rel="nofollow" @click="onReputationClicked(reputation)">
                        <div class="profile-reputation-widgets__widget-logo">
                            <ReputationLogo :reputation="reputation" />
                        </div>
                    </a>
                    <ReputationStat :reputation="reputation" />
                </div>
            </div>
            <EmptyContentSection
                v-else-if="$ff('profile-revamp')"
                icon="strategy"
                :title="$t('profile.section.reputation.empty.title')"
                :description="$t('profile.section.reputation.empty.subtitle')"
            >
                <template #action>
                    <ClientOnly>
                        <VJoyButton icon="add" size="small" variant="secondary" @click="editReputationWidgets">
                            {{ $t('profile.section.reputation.empty.cta.label') }}
                        </VJoyButton>
                        <template #fallback>
                            <VJoyFormFieldSkeleton size="small" width="250px" />
                        </template>
                    </ClientOnly>
                </template>
            </EmptyContentSection>
        </template>
    </MainComponent>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {computed, useTranslation} from '#imports';
    import {VJoyButton, VJoyIconButton, VJoyFormFieldSkeleton} from '@maltjoy/core-vue';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import EmptyContentSection from '~/components/section/EmptyContentSection.vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint, InfluenceLinkType} from '~/config/TrackingEvents';
    import ReputationLogo from '~/features/reputation-widgets/ReputationLogo.vue';
    import ReputationStat from '~/features/reputation-widgets/ReputationStat.vue';
    import {useProfileStore} from '~/store/profileStore';
    import {useReputationStore} from '~/store/reputation/reputationStore';
    import type {ExternalReputationResource} from '~/types/api-raw-types';

    const {t} = useTranslation();
    const {profile} = storeToRefs(useProfileStore());
    const hasReputation = computed(() => reputations.value.length > 0);
    const {reputations} = storeToRefs(useReputationStore());
    const {trackInfluenceLinkClicked} = useProfileTracking();

    function getTarget(reputation: ExternalReputationResource): string {
        return reputation.openLinkInCurrentTab ? '_self' : '_blank';
    }

    function editReputationWidgets() {
        eventBus.emit('reputation:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }

    function onReputationClicked(reputation: ExternalReputationResource) {
        trackInfluenceLinkClicked(getInfluenceLinkType(reputation), profile.value!.profileId);
    }

    function getInfluenceLinkType(reputation: ExternalReputationResource): InfluenceLinkType {
        switch (reputation.providerName) {
            case 'behance':
                return InfluenceLinkType.BEHANCE;
            case 'github':
                return InfluenceLinkType.GITHUB;
            case 'stackoverflow':
                return InfluenceLinkType.STACKOVERFLOW;
            case 'kaggle':
                return InfluenceLinkType.KAGGLE;
        }
        throw new Error(`Unknown reputation provider name: ${reputation.providerName}`);
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-reputation-widgets {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        row-gap: var(--joy-core-spacing-6);
        column-gap: var(--joy-core-spacing-6);

        &__widget {
            display: flex;
            flex-wrap: wrap;
            row-gap: var(--joy-core-spacing-2);
            column-gap: var(--joy-core-spacing-6);
            flex-direction: row;
            align-items: center;
        }
    }
</style>
